import React, { useEffect, useRef, useState } from "react";
import { Maximize2, Volume2, VolumeX, X } from "lucide-react";
import { fileUrl, posterUrl } from "../lib/api";

// Global mute switch shared by every FeedVideo. Default = sound ON.
// Muting one mutes all (they keep autoplaying); unmuting any unmutes all.
let globalMuted = false;
const muteListeners = new Set();
function setGlobalMuted(v) { if (globalMuted === v) return; globalMuted = v; muteListeners.forEach((l) => l(v)); }

export function FeedVideo({ path, className = "", wrapClassName = "mt-3", testid, autoplay = true }) {
  const ref = useRef(null);
  const inView = useRef(false);
  const userPaused = useRef(!autoplay); // gallery items start paused
  const [muted, setMuted] = useState(globalMuted);
  const [expanded, setExpanded] = useState(false);

  // Real first-frame poster (server-generated JPEG) so paused videos never show a black box.
  const src = fileUrl(path);
  const poster = posterUrl(path);

  const tryPlay = () => {
    const v = ref.current; if (!v) return;
    v.muted = globalMuted; setMuted(globalMuted);
    const p = v.play();
    if (p && p.catch) p.catch(() => { v.muted = true; setMuted(true); const p2 = v.play(); if (p2 && p2.catch) p2.catch(() => {}); });
  };

  // React to the global mute switch.
  useEffect(() => {
    const l = (v) => {
      setMuted(v);
      const el = ref.current; if (!el) return;
      el.muted = v;
      if (!v && !userPaused.current && inView.current) { const p = el.play(); if (p && p.catch) p.catch(() => {}); }
    };
    muteListeners.add(l);
    return () => muteListeners.delete(l);
  }, []);

  // Autoplay when scrolled into view (feed only).
  useEffect(() => {
    if (!autoplay) return;
    const el = ref.current; if (!el) return;
    const apply = () => {
      const v = ref.current; if (!v) return;
      if (inView.current && !userPaused.current) tryPlay();
      else v.pause();
    };
    const io = new IntersectionObserver(([e]) => {
      inView.current = e.isIntersecting && e.intersectionRatio >= 0.6;
      apply();
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, path]);

  const onTap = (e) => {
    e.stopPropagation();
    const v = ref.current; if (!v) return;
    if (v.paused) { userPaused.current = false; tryPlay(); }
    else { userPaused.current = true; v.pause(); }
  };

  return (
    <div className={`relative ${wrapClassName}`}>
      <video ref={ref} src={src} poster={poster} loop playsInline preload="metadata" onClick={onTap}
        className={`rounded-xl w-full cursor-pointer ${className}`} style={{ background: "#000" }}
        data-testid={testid} />
      <div className="absolute top-2 left-2 z-10">
        <button onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
          data-testid={testid ? `${testid}-expand` : "feed-video-expand"}
          className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }} aria-label="Expand">
          <Maximize2 size={14} color="#fff" />
        </button>
      </div>
      <div className="absolute bottom-2 right-2 flex items-center gap-1.5 z-10">
        <button onClick={(e) => { e.stopPropagation(); setGlobalMuted(!globalMuted); }}
          data-testid={testid ? `${testid}-mute` : "feed-video-mute"}
          className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }} aria-label={muted ? "Unmute" : "Mute"}>
          {muted ? <VolumeX size={15} color="#fff" /> : <Volume2 size={15} color="#fff" />}
        </button>
      </div>

      {expanded && (
        <div className="fixed inset-0 z-[75] grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.9)" }}
          onClick={() => setExpanded(false)} data-testid="feed-video-fullscreen">
          <button onClick={() => setExpanded(false)} className="absolute top-4 right-4 h-10 w-10 rounded-full grid place-items-center z-10"
            style={{ background: "rgba(255,255,255,0.15)" }} data-testid="feed-video-fullscreen-close">
            <X size={20} color="#fff" />
          </button>
          <video src={fileUrl(path)} controls autoPlay loop playsInline onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-2xl" style={{ background: "#000", maxHeight: "85vh" }} />
        </div>
      )}
    </div>
  );
}
